import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Sparkles, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function UserEditCard() {
  const { user } = useAuth();
  const [card, setCard] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/api/v1/user-cards/my-card")
      .then(setCard)
      .catch(() => toast.error("No card found for your account"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!card) return;
    setSaving(true);
    try {
      await api.put(`/api/v1/user-cards/${card.uid}`, card);
      toast.success("Card updated!");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateBio = async (lang: string) => {
    if (!card) return;
    setGenerating(true);
    try {
      const name = lang === "en" ? card.name_en : lang === "zh-cht" ? card.name_tc : card.name_sc;
      const title = lang === "en" ? card.title_en : lang === "zh-cht" ? card.title_tc : card.title_sc;
      const company = lang === "en" ? card.company_en : lang === "zh-cht" ? card.company_tc : card.company_sc;
      const { bio } = await api.post("/api/v1/ai-bio/generate", { name, title, company, language: lang });
      const field = lang === "en" ? "bio_en" : lang === "zh-cht" ? "bio_tc" : "bio_sc";
      setCard({ ...card, [field]: bio });
      toast.success("Bio generated!");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
    </div>
  );

  if (!card) return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4">
      <h1 className="text-2xl font-bold">No Card Found</h1>
      <p className="text-muted-foreground">No card is associated with your account.</p>
      <Link to="/" className="text-primary hover:underline flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>
    </div>
  );

  const updateField = (field: string, value: string) => setCard({ ...card, [field]: value });

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </Link>
            <h1 className="text-2xl font-bold">Edit Your Card</h1>
          </div>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save"}
          </button>
        </div>

        <div className="space-y-6">
          {/* English */}
          <div className="bg-card rounded-xl border border-border p-5">
            <h2 className="font-semibold mb-4">English</h2>
            <div className="grid gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Name</label>
                <input value={card.name_en || ""} onChange={(e) => updateField("name_en", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Title</label>
                <input value={card.title_en || ""} onChange={(e) => updateField("title_en", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Company</label>
                <input value={card.company_en || ""} onChange={(e) => updateField("company_en", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-muted-foreground">Bio</label>
                  <button onClick={() => handleGenerateBio("en")} disabled={generating} className="text-xs text-primary hover:underline flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Generate with AI
                  </button>
                </div>
                <textarea value={card.bio_en || ""} onChange={(e) => updateField("bio_en", e.target.value)} rows={3} className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-card rounded-xl border border-border p-5">
            <h2 className="font-semibold mb-4">Contact</h2>
            <div className="grid gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Email</label>
                <input value={card.email || ""} onChange={(e) => updateField("email", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Phone</label>
                <input value={card.phone || ""} onChange={(e) => updateField("phone", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Website</label>
                <input value={card.website || ""} onChange={(e) => updateField("website", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-background text-sm" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
